<template>
  <modal :title="title" persistent ref="modal">
    <q-stepper
      v-show="!isAdded"
      v-model="step"
      color="primary"
      done-color="positive"
      animated flat
    >
      <q-step :name="1" title="Details" :done="detailsStepDone">
        <details-step
          v-model:first-name="firstName"
          v-model:last-name="lastName"
          v-model:email="email"
          v-model:phone="phone"
          v-model:date-of-birth="dateOfBirth"
          v-model:file-ref-no="fileRefNo"
          v-model:date-of-loss="dateOfLoss"
          @done="detailsStepDone = $event"
        />
      </q-step>

      <q-step :name="2" title="Assign" :done="step > 2">
        <assign-step :account-id="customerId" v-model="assignTo" />
      </q-step>

      <q-step :name="3" title="Confirm" :done="step > 3">
        <confirm-step
          :first-name="firstName"
          :last-name="lastName"
          :email="email"
          :phone="phone"
          :date-of-birth="dateOfBirth"
          :file-ref-no="fileRefNo"
          :date-of-loss="dateOfLoss"
        />
      </q-step>
      <q-step
        :name="4"
        title="Payment"
        :done="paymentStepDone"
      >
        <payment-step
          :customer-id="customerId"
          v-model="paymentMethodId"
          @done="paymentStepDone = $event"
        />
      </q-step>
    </q-stepper>
    <div v-show="isAdded" class="animated fadeIn">
      <div class="text-subtitle1">
        {{ lastName}}, {{ firstName }} has been successfully added
        and has been sent an email asking them to connect their
        active social media accounts.
      </div>
      <div class="text-center q-my-md">
        <q-btn label="Add Another Client" color="primary" @click="addAnother" />
      </div>
    </div>
    <template v-if="!isAdded" v-slot:actions>
      <q-btn v-show="step > 1" label="Prev" @click="prev" />
      <q-btn
        v-show="step < 4"
        label="Next"
        color="primary"
        :disable="!canProceed"
        @click="next" />
      <async-btn
        v-show="step === 4"
        label="Add Client"
        color="primary"
        :task="task"
        :disable="!canAdd"
        @click="add" />
    </template>
  </modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ModalMixin } from '@/mixins';
import {
  Client, UserClient, User,
} from '@/type';
import Modal from '@/components/modal/Modal.vue';
import AsyncBtn from '@/components/AsyncBtn.vue';
import DetailsStep from './DetailsStep.vue';
import ConfirmStep from './ConfirmStep.vue';
import AssignStep from './AssignStep.vue';
import PaymentStep from './PaymentStep.vue';
import { add } from '../client';

interface Data {
  step: 1,
  detailsStepDone: boolean,
  paymentStepDone: boolean,
  isAdded: boolean,
  firstName: string,
  lastName: string,
  email: string,
  phone: string,
  dateOfBirth: string,
  fileRefNo?: string,
  dateOfLoss: string,
  assignTo: User[],
  paymentMethodId: number|null,
  task: Promise<Client>|Promise<UserClient>|null
}

export default defineComponent({
  emits: ['ok'],
  mixins: [ModalMixin],
  components: {
    Modal,
    DetailsStep,
    AssignStep,
    ConfirmStep,
    PaymentStep,
    AsyncBtn,
  },
  props: {
    customerId: {
      type: Number,
      required: true,
    },
    star: {
      type: Boolean,
      default: false,
    },
  },
  data(): Data {
    return {
      step: 1,
      detailsStepDone: false,
      paymentStepDone: false,
      isAdded: false,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      fileRefNo: undefined,
      dateOfLoss: '',
      assignTo: [],
      paymentMethodId: null,
      task: null,
    };
  },
  computed: {
    title(): string {
      let title = 'Add Client';
      if (this.firstName && this.lastName) {
        title += ` - ${this.lastName}, ${this.firstName}`;
      }
      return title;
    },
    canProceed(): boolean {
      return this.detailsStepDone;
    },
    canAdd(): boolean {
      return this.paymentStepDone;
    },
  },
  methods: {
    prev() {
      this.step -= 1;
    },
    next() {
      this.step += 1;
    },
    add() {
      this.task = add(
        this.customerId,
        this.firstName,
        this.lastName,
        this.email,
        this.phone,
        this.dateOfBirth,
        this.dateOfLoss,
        this.star,
        this.assignTo.map(u => u.id),
        this.fileRefNo,
        this.paymentMethodId,
      );
      this.task.then((client: Client) => {
        this.$emit('ok', client);
        this.isAdded = true;
      });
    },
    addAnother() {
      // @ts-ignore
      Object.assign(this.$data, this.$options.data());
    },
  },
});
</script>
